import { Component, h, Prop, State, Event, EventEmitter, Method, Fragment } from '@stencil/core';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { DropdownInputAttr, KeyValue } from './vs-dropdown.model';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';


@Component({
  tag: 'vs-dropdown',
  shadow: false,
})

export class VsDropdown {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of input field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription Input's type
   * @controlName type */
  @Prop() type: string = 'text';

  /**
   * @shortDescription Define the label for input field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription Label exists in the input field as eyebrow view.
   * @controlName eyebrow */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription If declared, it is the default option index.
   * @controlName defaultValue */
  @Prop() defaultValue: string = '';


  /**
   * @shortDescription Validators rules to check against for the input.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   * @shortDescription List of options to be rendered.
   * @controlName data */
  @Prop() data: any[] = [];

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   @shortDescription placeholder for the dropdown input
   @controlName placeholder
   */
  @Prop() placeholder: string = 'Please select';

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;

  /* END GENAI@GHCOPILOT */


  @State() showError: boolean = false;
  @State() value: string = '';
  @State() selectedErrorMessage: string = '';
  @State() valid: boolean = true;
  @State() inputAllyAlert: string = 'input_alert_';
  @State() isRequired: boolean;

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  private inputEl?;

  /**
   * @shortDescription Emit input value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    this.inputEl.selectedIndex = 0;
    this.value = this.defaultValue;
    this.updateInput(this.value);
    this.showError = isShowError;
  }

  /**
   * @shortDescription set input focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.showError = true;
    this.inputEl.focus();
  }

  componentWillLoad() {
    this.isRequired = this.validators.findIndex(validator => validator.key === VALIDATION_ATTRIBUTES.REQUIRE) > -1;
    this.inputAllyAlert += this.inputId;
    this.updateInput(this.defaultValue);
  }


  // START GENAI@CHATGPT4
  private getOptions(options: string[] | KeyValue[]): KeyValue[] {
    return options?.map((option: string | KeyValue, index: number) => {
      if (typeof option === 'string') {
        return { key: index.toString(), value: decodeHTMLEntities(option) };
      }
      if (option instanceof Object && option.hasOwnProperty('key') && option.hasOwnProperty('value')) {
        return { key: option.key, value: decodeHTMLEntities(option.value) };
      }
    });
  }

// END GENAI@CHATGPT4

  private onBlurInput = () => {
    this.showError = true;
    this.blurEvent.emit();
  };

  private validate = (value: string) => {
    const validationAttr = checkValidations(this.validators, value);
    this.selectedErrorMessage = validationAttr.errorMessage;
    this.valid = getValidStateAccordingToRequiredStatus(this.isRequired, value, validationAttr);
  };

  private onKeyupInput = () => {
    this.showError = true;
  };

  private onInputChange = (event) => {
    const { target } = event;
    this.updateInput(target.value.replace(/,/g, ''));
  };

  private updateInput = (value) => {
    this.value = value;
    this.validate(this.value);
    this.computedInputValue.emit(
      {
        index: this.index,
        key: this.inputId,
        value: this.value,
        isValid: this.valid,
        error: this.selectedErrorMessage,
      } as DropdownInputAttr,
    );
  };

  render() {
    return (
      <Fragment>
        {this.aboveInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.aboveInputContent} />}
        <div class={`${this.eyebrow ? 'vs-has-eyebrow' : ''}`}>
          <div class={`vs-input-group vs-height-auto vs-mb-1 ${this.eyebrow ? 'vs-eyebrow-group' : ''}`}>
            {this.label && (
              <label class={`vs-label ${this.eyebrow ? 'vs-label-eyebrow' : ''} vs-has-focus`}
                     htmlFor={this.inputId}>
                <span innerHTML={this.label} />
                {this.isRequired ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
              </label>
            )}
            {this.betweenInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.betweenInputContent} />}
            <select
              ref={el => this.inputEl = el as HTMLSelectElement}
              class={`vs-input ${this.showError && !this.valid ? 'vs-error' : ''}`}
              id={this.inputId}
              name={this.inputId}
              onChange={this.onInputChange}
              onBlur={this.onBlurInput}
              onKeyUp={this.onKeyupInput}
              aria-invalid={this.showError && !this.valid}
              aria-describedby={this.showError && !this.valid ? this.inputAllyAlert + this.inputId : ''}>
              <option value={null}>{this.placeholder}</option>
              {this.getOptions(this.data)?.map((optionValue: KeyValue) => (
                <option value={optionValue.key}>{optionValue.value}</option>
              ))}
            </select>
            {this.showError && !this.valid && this.selectedErrorMessage && (
              <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
                 id={this.inputAllyAlert + this.inputId}>
                <span aria-hidden='true' class='visa-icon-alert' />
                <span innerHTML={decodeHTMLEntities(this.selectedErrorMessage)} />
              </p>
            )}
          </div>
        </div>
        {this.belowInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  };
}
